/**
 * Handlers para la edición de Margen Bruto Zonal (MBZ).
 *
 * Este archivo contiene la lógica de negocio para obtener y actualizar un MBZ existente,
 * incluyendo conversión de datos, manejo de errores y navegación.
 */

import { AppRoutes } from "@/constants/routes";
import { handlePermissionError } from "@/utils/permission-check";
import { toaster } from "@/components/ui/toaster";
import { getAxiosErrorMessage } from "@/utils/axios-error-message";
import {
  getZonalGrossMarginDetail,
  updateZonalGrossMargin,
  ZonalGrossMarginDetail,
} from "@/services/zonal-gross-margin.service";
import { buildZonalGrossMarginRequestBody } from "../../utils/zonal-gross-margin-converters";

/**
 * Handler para obtener el detalle de un MBZ existente.
 *
 * Proceso:
 * 1. Llama al endpoint GET /MargenBrutoZonal/{Id}
 * 2. Setea el detalle en el estado del componente
 * 3. Maneja errores de permisos redirigiendo al login si es necesario
 *
 * @param setLoading - Función para setear estado de loading
 * @param setZonalGrossMargin - Función para setear el detalle en el estado
 * @param navigate - Función de navegación de React Router
 * @param fetchPermissions - Función para recargar permisos después de login
 * @param id - ID del MBZ a obtener
 */
export const fetchZonalGrossMarginDetailHandler = async ({
  setLoading,
  setZonalGrossMargin,
  navigate,
  fetchPermissions,
  id,
}: {
  setLoading: (loading: boolean) => void;
  setZonalGrossMargin: (data: ZonalGrossMarginDetail | null) => void;
  navigate: any;
  fetchPermissions: () => Promise<void>;
  id: string;
}) => {
  setLoading(true);
  try {
    const response = await getZonalGrossMarginDetail(Number(id));
    setZonalGrossMargin(response ?? null);
  } catch (error) {
    const handled = await handlePermissionError(
      error,
      navigate,
      fetchPermissions,
      AppRoutes.ZONAL_GROSS_MARGINS,
    );

    if (!handled) {
      toaster.create({
        type: "error",
        title: "No se pudo obtener el margen bruto zonal.",
      });
    }
    setZonalGrossMargin(null);
  } finally {
    setLoading(false);
  }
};

/**
 * Handler para actualizar un MBZ existente.
 *
 * Proceso:
 * 1. Convierte los valores del formulario al formato de la API (incluye ID)
 * 2. Llama al endpoint PUT /MargenBrutoZonal/{Id}
 * 3. Muestra notificación de éxito o error
 * 4. Ejecuta callback onSuccess si fue exitoso
 * 5. Maneja errores de permisos redirigiendo al login si es necesario
 *
 * @param body - Valores del formulario de edición
 * @param id - ID del MBZ a actualizar
 * @param navigate - Función de navegación de React Router
 * @param fetchPermissions - Función para recargar permisos después de login
 * @param onSuccess - Callback a ejecutar si la actualización fue exitosa
 * @throws Error si la actualización falla (para manejo en el componente)
 */
export const updateZonalGrossMarginHandler = async ({
  body,
  id,
  navigate,
  fetchPermissions,
  onSuccess,
}: {
  body: any;
  id: string;
  navigate: any;
  fetchPermissions: () => Promise<void>;
  onSuccess: () => void;
}) => {
  const requestBody = buildZonalGrossMarginRequestBody(body, Number(id));

  try {
    await updateZonalGrossMargin(Number(id), requestBody);
    toaster.create({
      type: "success",
      title: "Margen bruto zonal actualizado correctamente.",
    });
    onSuccess();
  } catch (error) {
    const handled = await handlePermissionError(
      error,
      navigate,
      fetchPermissions,
      AppRoutes.ZONAL_GROSS_MARGINS,
    );

    if (!handled) {
      toaster.create({
        type: "error",
        title: getAxiosErrorMessage(error, "No se pudo actualizar el margen bruto zonal."),
      });
    }
    throw error;
  }
};
